import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import { useTranslations } from "next-intl";
import { requestPasswordReset } from "@/services/api/auth";
import { apiErrorText } from "@/lib/api-error";
import { AUTH_ERROR_OPTIONS } from "@/lib/auth-error-options";

export function TenantForgotPasswordPage() {
  const t = useTranslations("auth");
  const tRoot = useTranslations();
  const { tenantSlug = "" } = useParams<{ tenantSlug: string }>();
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      await requestPasswordReset({ email, tenantSlug });
      setMessage(t("resetLinkSent"));
    } catch (err) {
      setError(apiErrorText(err, tRoot, "auth.requestFailed", AUTH_ERROR_OPTIONS));
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-4">
      <h1 className="mb-2 text-2xl font-semibold">{t("forgotPasswordTitle")}</h1>
      <p className="mb-6 text-sm text-[var(--text-muted)]">
        {t("tenantLabel", { tenant: tenantSlug })}
      </p>
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
        <div>
          <label htmlFor="email" className="mb-1 block text-sm font-medium">
            {t("email")}
          </label>
          <input
            id="email"
            data-testid="forgot-password-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-[var(--border-strong)] px-3 py-2"
          />
        </div>
        {error && (
          <p className="text-sm text-[var(--danger)]" data-testid="forgot-password-error">
            {error}
          </p>
        )}
        {message && (
          <p className="text-sm text-[var(--success)]" data-testid="forgot-password-success">
            {message}
          </p>
        )}
        <button
          type="submit"
          data-testid="forgot-password-submit"
          disabled={loading}
          className="w-full rounded-md bg-[var(--accent-strong)] px-4 py-2 text-[var(--accent-foreground)] hover:bg-[var(--accent-strong-hover)] disabled:opacity-50"
        >
          {loading ? t("sending") : t("sendResetLink")}
        </button>
      </form>
      <p className="mt-4 text-center text-sm">
        <Link to={`/t/${tenantSlug}/login`} className="text-[var(--text-heading)] underline">
          {t("backToSignIn")}
        </Link>
      </p>
    </main>
  );
}
